import { Chip } from '@heroui/react';
import { Button, cn } from '@/core/ui';
import type { BlogPostFieldsFragment } from '@/graphql/gql/graphql';
import { useBlog } from '@/store/blog';
import { BlogItemActions } from './BlogItemActions';

export interface BlogItemProps {
  index: number;
  className?: string;
  blogId?: string | null;
  /** When set, actions can open the editor with full post data */
  post?: BlogPostFieldsFragment | null;
  title: string;
  slug?: string | null;
  excerpt: string;
  imageSrc: string | null;
  tags: Array<{ id?: string | null; name?: string | null } | null>;
  updatedLabel: string;
}

export function BlogItem({
  index,
  className,
  blogId,
  post,
  title,
  slug,
  excerpt,
  imageSrc,
  tags,
  updatedLabel,
}: BlogItemProps) {
  const openBlogEditor = useBlog.use.openBlogEditor();
  const tagList = tags.filter(Boolean);
  const visibleTags = tagList.slice(0, 1);
  const overflowCount = tagList.length - 1;
  const normalizedSlug = slug?.replace(/^\/+/, '');
  const deployedBlogUrl = normalizedSlug ? `https://quesmed.com/blog/${normalizedSlug}-${blogId}` : null;
  const allTagsTitle = tagList
    .map((t) => t?.name)
    .filter((name): name is string => Boolean(name))
    .join(', ');

  const openPreviewFromTitle = () => {
    if (post) {
      openBlogEditor(post, { initialTab: 'preview' });
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col group relative',
        'shrink-0',
        'bg-content1',
        'rounded-lg',
        'border border-default-200',
        'hover:border-default-300',
        'transition-all duration-300',
        'shadow-sm backdrop-blur-xl',
        className,
      )}
    >
      <div className='p-3.5 flex flex-row gap-3'>
        <div className='relative shrink-0 w-28 h-24 rounded-md overflow-hidden bg-default-100 border border-default-200'>
          {imageSrc ? (
            <img src={imageSrc} alt={title} className='size-full object-cover' />
          ) : (
            <div className='size-full flex items-center justify-center text-default-400 text-[10px] px-1 text-center'>
              No image
            </div>
          )}
        </div>

        <div className='flex-1 min-w-0 flex flex-col gap-1.5'>
          <div className='flex items-start justify-between gap-2'>
            <div className='flex flex-col items-start min-w-0'>
              <div className='flex items-center gap-1'>
                <Chip
                  title={`Blog ${index}`}
                  color='default'
                  classNames={{
                    content: 'text-[10px]',
                    base: 'h-4.5 px-0.5 border border-default-200 rounded-md',
                  }}
                  className='capitalize'
                  size='sm'
                  variant='solid'
                >
                  {index}
                </Chip>
                {post ? (
                  <Button
                    type='button'
                    variant='light'
                    size='sm'
                    className='min-w-0 max-w-52 h-auto min-h-0 py-0 px-1 font-medium text-default-600 justify-start'
                    onPress={openPreviewFromTitle}
                  >
                    <span className='truncate text-left' title={title}>
                      {title}
                    </span>
                  </Button>
                ) : (
                  <span className='text-sm max-w-52 font-medium text-default-600 truncate' title={title}>
                    {title}
                  </span>
                )}
              </div>
              <div className='flex items-center gap-1.5 min-w-0 max-w-full'>
                {blogId ? (
                  <span className='text-[10px] leading-4 font-mono text-foreground-500 shrink-0'>
                    ID: {blogId}
                  </span>
                ) : null}
                {deployedBlogUrl ? (
                  <a
                    href={deployedBlogUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-[10px] leading-4 font-mono text-warning-500 truncate max-w-full hover:underline'
                    title={deployedBlogUrl}
                  >
                    {normalizedSlug}
                  </a>
                ) : null}
              </div>
            </div>
            <BlogItemActions blogId={blogId} post={post ?? undefined} title={title} />
          </div>

          <div className='mt-0.5'>
            <div
              className='h-0.5 w-full max-w-md rounded-full bg-linear-to-r from-pink-500 to-yellow-500'
              aria-hidden
            />
          </div>

          <p className='text-xs text-default-500 line-clamp-2' title={excerpt}>
            {excerpt}
          </p>

          <div className='flex items-center gap-1 flex-wrap mt-auto pt-0.5'>
            <div
              className='flex items-center gap-1 flex-wrap min-w-0'
              title={overflowCount > 0 ? allTagsTitle : undefined}
            >
              {visibleTags.map((t) => (
                <Chip
                  key={t?.id ?? t?.name}
                  radius='sm'
                  classNames={{ content: 'text-[10px]', base: 'h-5 px-1' }}
                  className='capitalize border border-default-200 bg-default-100/50'
                  size='sm'
                  variant='flat'
                >
                  {t?.name}
                </Chip>
              ))}
              {overflowCount > 0 ? (
                <Chip
                  key='tag-overflow'
                  radius='sm'
                  classNames={{ content: 'text-[10px]', base: 'h-5 px-1' }}
                  className='border border-default-200 bg-default-200/60 text-default-600'
                  size='sm'
                  variant='flat'
                >
                  +{overflowCount}
                </Chip>
              ) : null}
            </div>
            <span className='text-xs ml-auto text-default-400 whitespace-nowrap'>Updated {updatedLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
